import React, { useId } from 'react';
export type Cell = {
  id: string;
  icon?: React.FC<{ className?: string }>;
  label: string;
  valence: -2 | -1 | 1 | 2;
  arousal: -2 | -1 | 1 | 2;
  message?: string;
};

interface IconProps {
  className?: string;
}

// =============================================================================
// REUSABLE COMPONENTS
// =============================================================================

interface IconShadowFilterProps {
  filterId: string;
}

const IconShadowFilter: React.FC<IconShadowFilterProps> = ({ filterId }) => (
  <filter
    id={filterId}
    x="0"
    y="0"
    width="54"
    height="56"
    filterUnits="userSpaceOnUse"
    colorInterpolationFilters="sRGB"
  >
    <feFlood floodOpacity="0" result="BackgroundImageFix" />
    <feColorMatrix
      in="SourceAlpha"
      type="matrix"
      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
      result="hardAlpha"
    />
    <feOffset dy="1" />
    <feGaussianBlur stdDeviation="1.5" />
    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
    <feColorMatrix
      in="SourceAlpha"
      type="matrix"
      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
      result="hardAlpha"
    />
    <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect2_dropShadow" />
    <feOffset dy="1" />
    <feGaussianBlur stdDeviation="1" />
    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
    <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
    <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow" result="shape" />
  </filter>
);

const useIconIds = (prefix: string) => {
  const uniqueId = useId().replace(/:/g, '-');
  return {
    filterId: `${prefix}-filter-${uniqueId}`,
    maskInsideId: `${prefix}-mask-inside-${uniqueId}`,
    mask: (index: number) => `${prefix}-mask${index}-${uniqueId}`,
    maskLuminanceId: `${prefix}-mask-lum-${uniqueId}`,
    uniqueId,
  };
};

interface IconBaseProps {
  maskInsideId: string;
  children: React.ReactNode;
}

const IconBase: React.FC<IconBaseProps> = ({ maskInsideId, children }) => (
  <>
    <mask id={maskInsideId} fill="white">
      <path d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z" />
    </mask>
    <path
      d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z"
      fill="currentColor"
    />
    <path
      d="M11 2V6H43V2V-2H11V2ZM51 10H47V44H51H55V10H51ZM43 52V48H11V52V56H43V52ZM3 44H7V10H3H-1V44H3ZM11 52V48C8.79086 48 7 46.2091 7 44H3H-1C-1 50.6274 4.37258 56 11 56V52ZM51 44H47C47 46.2091 45.2091 48 43 48V52V56C49.6274 56 55 50.6274 55 44H51ZM43 2V6C45.2091 6 47 7.79086 47 10H51H55C55 3.37258 49.6274 -2 43 -2V2ZM11 2V-2C4.37258 -2 -1 3.37259 -1 10H3H7C7 7.79086 8.79086 6 11 6V2Z"
      fill="var(--icon-border, #EBE4DC)"
      mask={`url(#${maskInsideId})`}
    />
    {children}
  </>
);

interface StandardEmotionIconProps extends IconProps {
  prefix: string;
  color: string;
  children: React.ReactNode;
}

const StandardEmotionIcon: React.FC<StandardEmotionIconProps> = ({ prefix, color, className = '', children }) => {
  const { filterId, maskInsideId } = useIconIds(prefix);

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color }}
    >
      <g filter={`url(#${filterId})`}>
        <IconBase maskInsideId={maskInsideId}>{children}</IconBase>
      </g>
      <defs>
        <IconShadowFilter filterId={filterId} />
      </defs>
    </svg>
  );
};

// =============================================================================
// SMALL ICON COMPONENTS (48x50 viewBox, no filter)
// =============================================================================

interface SmallIconBaseProps {
  maskInsideId: string;
  children: React.ReactNode;
}

const SmallIconBase: React.FC<SmallIconBaseProps> = ({ maskInsideId, children }) => (
  <>
    <mask id={maskInsideId} fill="white">
      <path d="M0 8C0 3.58172 3.58172 0 8 0H40C44.4183 0 48 3.58172 48 8V42C48 46.4183 44.4183 50 40 50H8C3.58172 50 0 46.4183 0 42V8Z" />
    </mask>
    <path
      d="M0 8C0 3.58172 3.58172 0 8 0H40C44.4183 0 48 3.58172 48 8V42C48 46.4183 44.4183 50 40 50H8C3.58172 50 0 46.4183 0 42V8Z"
      fill="currentColor"
    />
    <path
      d="M8 0V4H40V0V-4H8V0ZM48 8H44V42H48H52V8H48ZM40 50V46H8V50V54H40V50ZM0 42H4V8H0H-4V42H0ZM8 50V46C5.79086 46 4 44.2091 4 42H0H-4C-4 48.6274 1.37258 54 8 54V50ZM48 42H44C44 44.2091 42.2091 46 40 46V50V54C46.6274 54 52 48.6274 52 42H48ZM40 0V4C42.2091 4 44 5.79086 44 8H48H52C52 1.37258 46.6274 -4 40 -4V0ZM8 0V-4C1.37258 -4 -4 1.37259 -4 8H0H4C4 5.79086 5.79086 4 8 4V0Z"
      fill="var(--icon-border, #EBE4DC)"
      mask={`url(#${maskInsideId})`}
    />
    {children}
  </>
);

// =============================================================================
// ROW 0: RED/ANGRY EMOTIONS
// =============================================================================

/**
 * AngryFaceIcon - Actual angry face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const AngryFaceIcon: React.FC<IconProps> = ({ className = '' }) => {
  // Generate unique IDs for this instance
  const uniqueId = useId().replace(/:/g, '-');
  const filterId = `angry-filter-${uniqueId}`;
  const maskInsideId = `angry-mask-inside-${uniqueId}`;
  const maskLuminanceId = `angry-mask-lum-${uniqueId}`;

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#E33434' }}
    >
      <g filter={`url(#${filterId})`}>
        <mask id={maskInsideId} fill="white">
          <path d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z" />
        </mask>

        <path
          d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z"
          fill="currentColor"
        />
        <path
          d="M11 2V6H43V2V-2H11V2ZM51 10H47V44H51H55V10H51ZM43 52V48H11V52V56H43V52ZM3 44H7V10H3H-1V44H3ZM11 52V48C8.79086 48 7 46.2091 7 44H3H-1C-1 50.6274 4.37258 56 11 56V52ZM51 44H47C47 46.2091 45.2091 48 43 48V52V56C49.6274 56 55 50.6274 55 44H51ZM43 2V6C45.2091 6 47 7.79086 47 10H51H55C55 3.37258 49.6274 -2 43 -2V2ZM11 2V-2C4.37258 -2 -1 3.37259 -1 10H3H7C7 7.79086 8.79086 6 11 6V2Z"
          fill="var(--icon-border, #EBE4DC)"
          mask={`url(#${maskInsideId})`}
        />
        <path
          d="M20.4879 30.0647C23.5187 30.0647 25.9757 27.6077 25.9757 24.5769C25.9757 21.546 23.5187 19.089 20.4879 19.089C17.457 19.089 15 21.546 15 24.5769C15 27.6077 17.457 30.0647 20.4879 30.0647Z"
          fill="white"
        />
        <path
          d="M32.6011 29.9757C35.632 29.9757 38.089 27.5187 38.089 24.4879C38.089 21.457 35.632 19 32.6011 19C29.5703 19 27.1133 21.457 27.1133 24.4879C27.1133 27.5187 29.5703 29.9757 32.6011 29.9757Z"
          fill="white"
        />
        <path
          d="M32.456 26.7733C33.7741 26.7733 34.8426 25.7048 34.8426 24.3866C34.8426 23.0685 33.7741 22 32.456 22C31.1379 22 30.0693 23.0685 30.0693 24.3866C30.0693 25.7048 31.1379 26.7733 32.456 26.7733Z"
          fill="#030303"
        />
        <path
          d="M20.3534 26.7733C21.6715 26.7733 22.7401 25.7048 22.7401 24.3866C22.7401 23.0685 21.6715 22 20.3534 22C19.0353 22 17.9668 23.0685 17.9668 24.3866C17.9668 25.7048 19.0353 26.7733 20.3534 26.7733Z"
          fill="#030303"
        />
        <path
          d="M20.9335 24.3306C21.1534 24.1275 21.1873 23.8066 21.0093 23.6138C20.8313 23.421 20.5087 23.4293 20.2888 23.6323C20.0689 23.8354 20.035 24.1563 20.213 24.3491C20.3911 24.5419 20.7136 24.5336 20.9335 24.3306Z"
          fill="white"
        />
        <path
          d="M33.0371 24.3306C33.2569 24.1275 33.2909 23.8066 33.1128 23.6138C32.9348 23.421 32.6122 23.4293 32.3923 23.6323C32.1724 23.8354 32.1385 24.1563 32.3165 24.3491C32.4946 24.5419 32.8172 24.5336 33.0371 24.3306Z"
          fill="white"
        />
        <path
          d="M20.532 21.2935C23.4405 22.1696 26.1504 21.7107 26.5849 20.2685C27.0193 18.8263 25.0136 16.947 22.1051 16.0709C19.1967 15.1948 16.4867 15.6537 16.0523 17.0959C15.6179 18.5381 17.6235 20.4175 20.532 21.2935Z"
          fill="#E33434"
        />
        <path
          d="M32.2459 21.2934C34.7706 20.3367 36.4424 18.5719 35.98 17.3516C35.5175 16.1313 33.096 15.9176 30.5713 16.8743C28.0467 17.831 26.3749 19.5958 26.8373 20.8161C27.2997 22.0364 29.7212 22.2501 32.2459 21.2934Z"
          fill="#E33434"
        />
        <path
          d="M25.548 21.4649L15.1083 18.6177C14.7324 18.4992 14.3319 18.7075 14.2138 19.0828C14.0956 19.4581 14.3045 19.8584 14.6804 19.9768L25.1201 22.824C25.4959 22.9424 25.8964 22.7342 26.0145 22.3589C26.1327 21.9836 25.9238 21.5833 25.548 21.4649Z"
          fill="#000308"
        />
        <path
          d="M37.2197 18.5829L26.7915 21.4718C26.4079 21.5621 26.1702 21.9458 26.2604 22.3288C26.3507 22.7117 26.7348 22.949 27.1184 22.8587L37.5466 19.9698C37.9302 19.8795 38.168 19.4958 38.0777 19.1129C37.9875 18.7299 37.6033 18.4926 37.2197 18.5829Z"
          fill="#000308"
        />
        <path
          d="M34.8917 39L19.1094 39C18.4971 39 18 38.7831 18 38.5159L18 36.927C18 34.7596 22.0326 33 27 33C31.9674 33 36 34.7596 36 36.927L36 38.5164C36 38.7836 35.5029 39 34.8917 39Z"
          fill="#030303"
        />

        <mask
          id={maskLuminanceId}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="18"
          y="33"
          width="18"
          height="6"
        >
          <path
            d="M34.8917 39L19.1094 39C18.4971 39 18 38.7831 18 38.5159L18 36.927C18 34.7596 22.0326 33 27 33C31.9674 33 36 34.7596 36 36.927L36 38.5164C36 38.7836 35.5029 39 34.8917 39Z"
            fill="white"
          />
        </mask>

        <g mask={`url(#${maskLuminanceId})`}>
          <path
            d="M21.511 36H21.471C20.6586 36 20 36.6586 20 37.471V38.8067C20 39.6191 20.6586 40.2776 21.471 40.2776H21.511C22.3234 40.2776 22.982 39.6191 22.982 38.8067V37.471C22.982 36.6586 22.3234 36 21.511 36Z"
            fill="white"
          />
          <path
            d="M26.4564 37.487C26.4564 36.6657 25.7906 36 24.9694 36C24.1482 36 23.4824 36.6657 23.4824 37.487V38.7906C23.4824 39.6119 24.1482 40.2776 24.9694 40.2776C25.7906 40.2776 26.4564 39.6119 26.4564 38.7906V37.487Z"
            fill="white"
          />
          <path
            d="M28.4671 36H28.427C27.6146 36 26.9561 36.6586 26.9561 37.471V38.8067C26.9561 39.6191 27.6146 40.2776 28.427 40.2776H28.4671C29.2795 40.2776 29.938 39.6191 29.938 38.8067V37.471C29.938 36.6586 29.2795 36 28.4671 36Z"
            fill="white"
          />
          <path
            d="M33.4115 37.487C33.4115 36.6657 32.7457 36 31.9245 36C31.1032 36 30.4375 36.6657 30.4375 37.487V38.7906C30.4375 39.6119 31.1032 40.2776 31.9245 40.2776C32.7457 40.2776 33.4115 39.6119 33.4115 38.7906V37.487Z"
            fill="white"
          />
          <path
            d="M21.511 30H21.471C20.6586 30 20 30.6586 20 31.471V32.8067C20 33.6191 20.6586 34.2776 21.471 34.2776H21.511C22.3234 34.2776 22.982 33.6191 22.982 32.8067V31.471C22.982 30.6586 22.3234 30 21.511 30Z"
            fill="white"
          />
          <path
            d="M26.4564 32.487C26.4564 31.6657 25.7906 31 24.9694 31C24.1482 31 23.4824 31.6657 23.4824 32.487V33.7906C23.4824 34.6119 24.1482 35.2776 24.9694 35.2776C25.7906 35.2776 26.4564 34.6119 26.4564 33.7906V32.487Z"
            fill="white"
          />
          <path
            d="M28.4671 31H28.427C27.6146 31 26.9561 31.6586 26.9561 32.471V33.8067C26.9561 34.6191 27.6146 35.2776 28.427 35.2776H28.4671C29.2795 35.2776 29.938 34.6191 29.938 33.8067V32.471C29.938 31.6586 29.2795 31 28.4671 31Z"
            fill="white"
          />
          <path
            d="M33.4115 31.487C33.4115 30.6657 32.7457 30 31.9245 30C31.1032 30 30.4375 30.6657 30.4375 31.487V32.7906C30.4375 33.6119 31.1032 34.2776 31.9245 34.2776C32.7457 34.2776 33.4115 33.6119 33.4115 32.7906V31.487Z"
            fill="white"
          />
        </g>

        <path
          d="M37.2449 13.6438L38.2497 10.8171C38.3977 10.4008 38.8549 10.1834 39.2712 10.3313C39.6875 10.4793 39.9049 10.9365 39.7569 11.3528L38.7522 14.1796C38.6042 14.5959 38.147 14.8133 37.7307 14.6653C37.3144 14.5173 37.097 14.0601 37.2449 13.6438Z"
          fill="#000308"
        />
        <path
          d="M37.3611 11.2393L40.1878 12.2441C40.6041 12.3921 40.8215 12.8493 40.6735 13.2656C40.5256 13.6819 40.0684 13.8993 39.6521 13.7513L36.8253 12.7466C36.409 12.5986 36.1916 12.1414 36.3396 11.7251C36.4875 11.3088 36.9447 11.0914 37.3611 11.2393Z"
          fill="#000308"
        />
      </g>

      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="54"
          height="56"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_9028_5088" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect2_dropShadow_9028_5088" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_9028_5088" result="effect2_dropShadow_9028_5088" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_9028_5088" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

/**
 * AnnoyedIcon - Annoyed face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const AnnoyedIcon: React.FC<IconProps> = ({ className = '' }) => {
  // Generate unique IDs for this instance
  const uniqueId = useId().replace(/:/g, '-');
  const filterId = `annoyed-filter-${uniqueId}`;
  const maskInsideId = `annoyed-mask-inside-${uniqueId}`;

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#E33434' }}
    >
      <g filter={`url(#${filterId})`}>
        <mask id={maskInsideId} fill="white">
          <path d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z" />
        </mask>

        <path
          d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z"
          fill="currentColor"
        />
        <path
          d="M11 2V6H43V2V-2H11V2ZM51 10H47V44H51H55V10H51ZM43 52V48H11V52V56H43V52ZM3 44H7V10H3H-1V44H3ZM11 52V48C8.79086 48 7 46.2091 7 44H3H-1C-1 50.6274 4.37258 56 11 56V52ZM51 44H47C47 46.2091 45.2091 48 43 48V52V56C49.6274 56 55 50.6274 55 44H51ZM43 2V6C45.2091 6 47 7.79086 47 10H51H55C55 3.37258 49.6274 -2 43 -2V2ZM11 2V-2C4.37258 -2 -1 3.37259 -1 10H3H7C7 7.79086 8.79086 6 11 6V2Z"
          fill="var(--icon-border, #EBE4DC)"
          mask={`url(#${maskInsideId})`}
        />
        <path
          d="M23.5042 34.7792C23.1599 35.1236 22.6021 35.1236 22.2578 34.7792C21.9135 34.4349 21.9135 33.8771 22.2578 33.5328C23.4242 32.3664 25.3068 31.9648 26.983 32.0024C28.6602 32.0401 30.4635 32.5246 31.5344 33.5064C31.8934 33.8354 31.9176 34.3926 31.5886 34.7515C31.2595 35.1105 30.7023 35.1347 30.3434 34.8057C29.718 34.2324 28.4114 33.7981 26.9433 33.7651C25.4741 33.7321 24.1756 34.1079 23.5042 34.7792Z"
          fill="#1B1B19"
        />
        <path
          d="M21.4879 30.0647C24.5187 30.0647 26.9757 27.6077 26.9757 24.5769C26.9757 21.546 24.5187 19.089 21.4879 19.089C18.457 19.089 16 21.546 16 24.5769C16 27.6077 18.457 30.0647 21.4879 30.0647Z"
          fill="white"
        />
        <path
          d="M33.6011 29.9757C36.632 29.9757 39.089 27.5187 39.089 24.4879C39.089 21.457 36.632 19 33.6011 19C30.5703 19 28.1133 21.457 28.1133 24.4879C28.1133 27.5187 30.5703 29.9757 33.6011 29.9757Z"
          fill="white"
        />
        <path
          d="M33.456 26.3193C34.7741 26.3193 35.8426 25.2508 35.8426 23.9327C35.8426 22.6146 34.7741 21.546 33.456 21.546C32.1379 21.546 31.0693 22.6146 31.0693 23.9327C31.0693 25.2508 32.1379 26.3193 33.456 26.3193Z"
          fill="#030303"
        />
        <path
          d="M21.3534 26.3193C22.6715 26.3193 23.7401 25.2508 23.7401 23.9327C23.7401 22.6146 22.6715 21.546 21.3534 21.546C20.0353 21.546 18.9668 22.6146 18.9668 23.9327C18.9668 25.2508 20.0353 26.3193 21.3534 26.3193Z"
          fill="#030303"
        />
        <path
          d="M21.9335 23.0819C22.1534 22.8789 22.1873 22.558 22.0093 22.3651C21.8313 22.1723 21.5087 22.1806 21.2888 22.3837C21.0689 22.5867 21.035 22.9076 21.213 23.1005C21.3911 23.2933 21.7136 23.285 21.9335 23.0819Z"
          fill="white"
        />
        <path
          d="M34.0371 22.993C34.2569 22.79 34.2909 22.4691 34.1128 22.2763C33.9348 22.0835 33.6122 22.0918 33.3923 22.2948C33.1724 22.4979 33.1385 22.8188 33.3165 23.0116C33.4946 23.2044 33.8172 23.1961 34.0371 22.993Z"
          fill="white"
        />
        <path
          d="M24.5467 19.0224L18.8966 18.2643C18.5061 18.2119 18.1471 18.4856 18.0949 18.8756C18.0427 19.2656 18.317 19.6242 18.7076 19.6766L24.3577 20.4347C24.7483 20.4871 25.1072 20.2134 25.1594 19.8234C25.2116 19.4334 24.9373 19.0748 24.5467 19.0224Z"
          fill="#000308"
        />
        <path
          d="M35.3108 18.1276L29.702 19.1475C29.3143 19.218 29.057 19.589 29.1272 19.9761C29.1975 20.3633 29.5688 20.62 29.9565 20.5495L35.5653 19.5296C35.953 19.4591 36.2103 19.0881 36.14 18.7009C36.0698 18.3138 35.6985 18.0571 35.3108 18.1276Z"
          fill="#000308"
        />
      </g>

      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="54"
          height="56"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_9028_5077" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect2_dropShadow_9028_5077" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_9028_5077" result="effect2_dropShadow_9028_5077" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_9028_5077" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

/**
 * AnxiousIcon - Anxious face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const AnxiousIcon: React.FC<IconProps> = ({ className = '' }) => {
  // Generate unique IDs for this instance
  const uniqueId = useId().replace(/:/g, '-');
  const filterId = `anxious-filter-${uniqueId}`;
  const maskInsideId = `anxious-mask-inside-${uniqueId}`;
  const mask0Id = `anxious-mask0-${uniqueId}`;
  const mask1Id = `anxious-mask1-${uniqueId}`;

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#E33434' }}
    >
      <g filter={`url(#${filterId})`}>
        <mask id={maskInsideId} fill="white">
          <path d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z" />
        </mask>

        <path
          d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z"
          fill="currentColor"
        />
        <path
          d="M11 2V6H43V2V-2H11V2ZM51 10H47V44H51H55V10H51ZM43 52V48H11V52V56H43V52ZM3 44H7V10H3H-1V44H3ZM11 52V48C8.79086 48 7 46.2091 7 44H3H-1C-1 50.6274 4.37258 56 11 56V52ZM51 44H47C47 46.2091 45.2091 48 43 48V52V56C49.6274 56 55 50.6274 55 44H51ZM43 2V6C45.2091 6 47 7.79086 47 10H51H55C55 3.37258 49.6274 -2 43 -2V2ZM11 2V-2C4.37258 -2 -1 3.37259 -1 10H3H7C7 7.79086 8.79086 6 11 6V2Z"
          fill="var(--icon-border, #EBE4DC)"
          mask={`url(#${maskInsideId})`}
        />
        <path
          d="M34.5 30C38.0898 30 41 28.2091 41 26C41 23.7909 38.0898 22 34.5 22C30.9101 22 28 23.7909 28 26C28 28.2091 30.9101 30 34.5 30Z"
          fill="white"
        />
        <path
          d="M34.2231 29.4463C36.0032 29.4463 37.4463 28.0032 37.4463 26.2232C37.4463 24.4431 36.0032 23 34.2231 23C32.4431 23 31 24.4431 31 26.2232C31 28.0032 32.4431 29.4463 34.2231 29.4463Z"
          fill="black"
        />
        <circle cx="32.7891" cy="24.2893" r="1" fill="white" />
        <path
          d="M19.5 30C23.0898 30 26 28.2091 26 26C26 23.7909 23.0898 22 19.5 22C15.9101 22 13 23.7909 13 26C13 28.2091 15.9101 30 19.5 30Z"
          fill="white"
        />
        <path
          d="M19.2231 29.4463C21.0032 29.4463 22.4463 28.0032 22.4463 26.2232C22.4463 24.4431 21.0032 23 19.2231 23C17.4431 23 16 24.4431 16 26.2232C16 28.0032 17.4431 29.4463 19.2231 29.4463Z"
          fill="black"
        />
        <circle cx="17.7891" cy="24.2893" r="1" fill="white" />
        <path d="M14 23C15 22.7321 17.5566 22.5 19 20" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M40 23C39 22.7321 36.4434 22.5 35 20" stroke="black" strokeWidth="1.6" strokeLinecap="round" />

        <mask id={mask0Id} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="39" y="23" width="6" height="10">
          <path
            d="M41.9766 23C41.3034 23 35.6294 32.8093 41.9766 32.8093C48.3239 32.8093 42.8422 23 41.9766 23Z"
            fill="#D9D9D9"
          />
        </mask>

        <g mask={`url(#${mask0Id})`}>
          <path
            d="M41.9766 23C41.3034 23 35.6294 32.8093 41.9766 32.8093C48.3239 32.8093 42.8422 23 41.9766 23Z"
            fill="#36C2DB"
          />
          <path
            d="M43.2231 26.4972L43.6798 27.4916C43.806 27.7662 43.6856 28.091 43.411 28.2171C43.1364 28.3433 42.8116 28.2229 42.6855 27.9483L42.2288 26.954C42.1026 26.6794 42.223 26.3545 42.4976 26.2284C42.7722 26.1023 43.097 26.2227 43.2231 26.4972Z"
            fill="#B8EEFD"
          />
          <path
            d="M42.0774 30.8859C38.4229 30.8859 39.3846 26.398 40.6348 24.154C39.9616 24.9875 38.6152 27.2315 38.6152 29.5395C38.6152 32.4246 40.8271 33.2902 42.0774 33.2902C43.2314 33.2902 44.9625 32.5208 45.0586 29.5395C45.1356 27.1545 43.6802 24.8272 42.9429 23.9617C44.3854 26.2698 45.7318 30.8859 42.0774 30.8859Z"
            fill="#2CA4B9"
          />
        </g>

        <mask id={mask1Id} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="23" y="33" width="8" height="5">
          <path
            d="M31 35.5C31 36.8807 29.2091 37.5 27 37.5C24.7909 37.5 23 36.8807 23 35.5C23 34.1193 24.7909 33 27 33C29.2091 33 31 34.1193 31 35.5Z"
            fill="black"
          />
        </mask>

        <g mask={`url(#${mask1Id})`}>
          <path
            d="M31 35.5C31 36.8807 29.2091 37.5 27 37.5C24.7909 37.5 23 36.8807 23 35.5C23 34.1193 24.7909 33 27 33C29.2091 33 31 34.1193 31 35.5Z"
            fill="black"
          />
          <path
            d="M25.511 31H25.471C24.6586 31 24 31.6586 24 32.471V33.8067C24 34.6191 24.6586 35.2776 25.471 35.2776H25.511C26.3234 35.2776 26.982 34.6191 26.982 33.8067V32.471C26.982 31.6586 26.3234 31 25.511 31Z"
            fill="white"
          />
          <path
            d="M30.349 32.487C30.349 31.6657 29.6832 31 28.862 31C28.0407 31 27.375 31.6657 27.375 32.487V33.7906C27.375 34.6119 28.0407 35.2776 28.862 35.2776C29.6832 35.2776 30.349 34.6119 30.349 33.7906V32.487Z"
            fill="white"
          />
        </g>
      </g>

      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="54"
          height="56"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_9028_5128" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect2_dropShadow_9028_5128" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_9028_5128" result="effect2_dropShadow_9028_5128" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_9028_5128" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

/**
 * PanickedIcon - Panicked face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const PanickedIcon: React.FC<IconProps> = ({ className = '' }) => {
  // Generate unique IDs for this instance
  const uniqueId = useId().replace(/:/g, '-');
  const filterId = `panicked-filter-${uniqueId}`;
  const maskInsideId = `panicked-mask-inside-${uniqueId}`;
  const maskLuminanceId = `panicked-mask-lum-${uniqueId}`;

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#E33434' }}
    >
      <g filter={`url(#${filterId})`}>
        <mask id={maskInsideId} fill="white">
          <path d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z" />
        </mask>

        <path
          d="M3 10C3 5.58172 6.58172 2 11 2H43C47.4183 2 51 5.58172 51 10V44C51 48.4183 47.4183 52 43 52H11C6.58172 52 3 48.4183 3 44V10Z"
          fill="currentColor"
        />
        <path
          d="M11 2V6H43V2V-2H11V2ZM51 10H47V44H51H55V10H51ZM43 52V48H11V52V56H43V52ZM3 44H7V10H3H-1V44H3ZM11 52V48C8.79086 48 7 46.2091 7 44H3H-1C-1 50.6274 4.37258 56 11 56V52ZM51 44H47C47 46.2091 45.2091 48 43 48V52V56C49.6274 56 55 50.6274 55 44H51ZM43 2V6C45.2091 6 47 7.79086 47 10H51H55C55 3.37258 49.6274 -2 43 -2V2ZM11 2V-2C4.37258 -2 -1 3.37259 -1 10H3H7C7 7.79086 8.79086 6 11 6V2Z"
          fill="var(--icon-border, #EBE4DC)"
          mask={`url(#${maskInsideId})`}
        />
        <path
          d="M34.5 30C38.0898 30 41 28.2091 41 26C41 23.7909 38.0898 22 34.5 22C30.9101 22 28 23.7909 28 26C28 28.2091 30.9101 30 34.5 30Z"
          fill="white"
        />
        <path
          d="M34.2231 29.4463C36.0032 29.4463 37.4463 28.0032 37.4463 26.2232C37.4463 24.4431 36.0032 23 34.2231 23C32.4431 23 31 24.4431 31 26.2232C31 28.0032 32.4431 29.4463 34.2231 29.4463Z"
          fill="black"
        />
        <circle cx="34.7891" cy="26.2893" r="1" fill="white" />
        <path
          d="M19.5 30C23.0898 30 26 28.2091 26 26C26 23.7909 23.0898 22 19.5 22C15.9101 22 13 23.7909 13 26C13 28.2091 15.9101 30 19.5 30Z"
          fill="white"
        />
        <path
          d="M19.2231 29.4463C21.0032 29.4463 22.4463 28.0032 22.4463 26.2232C22.4463 24.4431 21.0032 23 19.2231 23C17.4431 23 16 24.4431 16 26.2232C16 28.0032 17.4431 29.4463 19.2231 29.4463Z"
          fill="black"
        />
        <circle cx="19.7891" cy="26.2893" r="1" fill="white" />
        <path
          d="M17.1093 23.0836C19.9381 21.9767 21.7863 19.9424 21.2375 18.5398C20.6887 17.1371 17.9506 16.8973 15.1219 18.0041C12.2931 19.1109 10.4449 21.1453 10.9937 22.5479C11.5425 23.9505 14.2806 24.1904 17.1093 23.0836Z"
          fill="#E33434"
        />
        <path
          d="M36.313 23.178C39.247 23.9642 41.9416 23.4221 42.3314 21.9673C42.7213 20.5124 40.6588 18.6956 37.7247 17.9095C34.7906 17.1233 32.0961 17.6654 31.7063 19.1202C31.3164 20.5751 33.3789 22.3918 36.313 23.178Z"
          fill="#E33434"
        />
        <path
          d="M14 20.415C15.5 20.415 15.2453 19.0934 16.4199 18.415C17.1498 17.9935 18.4468 18.3733 19 17.415"
          stroke="black"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M34.9996 17.5C35.7496 18.799 36.7668 17.9176 37.9416 18.5957C38.6717 19.0171 38.9912 20.3301 40.0977 20.3301"
          stroke="black"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M33.0179 40C33.0179 40 29.4677 39 27.0002 39C24.5328 39 20.7428 40 20.7428 40C20.2665 40 19.8799 39.7469 19.8799 39.4352L19.8799 37.5815C19.8799 35.0528 23.0163 33 26.8799 33C30.7434 33 33.8799 35.0528 33.8799 37.5815L33.8799 39.4358C33.8799 39.7475 33.4933 40 33.0179 40Z"
          fill="#030303"
        />

        <mask
          id={maskLuminanceId}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="20"
          y="33"
          width="14"
          height="7"
        >
          <path
            d="M33.138 40C33.138 40 29.4672 38.9999 27 39C24.5328 39.0001 20.8629 40 20.8629 40C20.3866 40 20 39.7469 20 39.4352L20 37.5815C20 35.0528 23.1365 33 27 33C30.8635 33 34 35.0528 34 37.5815L34 39.4358C34 39.7475 33.6134 40 33.138 40Z"
            fill="white"
          />
        </mask>

        <g mask={`url(#${maskLuminanceId})`}>
          <path
            d="M25.1253 31.4606H25.0852C24.2728 31.4606 23.6143 32.1191 23.6143 32.9315V34.2672C23.6143 35.0796 24.2728 35.7382 25.0852 35.7382H25.1253C25.9377 35.7382 26.5962 35.0796 26.5962 34.2672V32.9315C26.5962 32.1191 25.9377 31.4606 25.1253 31.4606Z"
            fill="white"
          />
          <path
            d="M29.9632 32.9476C29.9632 32.1263 29.2975 31.4606 28.4762 31.4606C27.655 31.4606 26.9893 32.1263 26.9893 32.9476V34.2512C26.9893 35.0725 27.655 35.7382 28.4762 35.7382C29.2975 35.7382 29.9632 35.0725 29.9632 34.2512V32.9476Z"
            fill="white"
          />
        </g>

        <path
          d="M33.2728 12.9828L36.5286 9.06926C36.6483 8.92539 36.8824 9.01003 36.8824 9.19717V11.444C36.8824 11.5545 36.9719 11.644 37.0824 11.644H38.5759C38.745 11.644 38.8378 11.8408 38.7302 11.9713L35.4851 15.9074C35.3473 16.0745 35.0793 15.934 35.1384 15.7256L35.7513 13.5653C35.7875 13.4376 35.6916 13.3107 35.5589 13.3107H33.4265C33.2571 13.3107 33.1644 13.1131 33.2728 12.9828Z"
          fill="#FCB815"
        />
        <path
          d="M19.7272 12.9828L16.4714 9.06926C16.3517 8.92539 16.1176 9.01003 16.1176 9.19717V11.444C16.1176 11.5545 16.0281 11.644 15.9176 11.644H14.4241C14.255 11.644 14.1622 11.8408 14.2698 11.9713L17.5149 15.9074C17.6527 16.0745 17.9207 15.934 17.8616 15.7256L17.2487 13.5653C17.2125 13.4376 17.3084 13.3107 17.4411 13.3107H19.5735C19.7429 13.3107 19.8356 13.1131 19.7272 12.9828Z"
          fill="#FCB815"
        />
        <path
          d="M29.5452 14.1947L28.3911 7.34055C28.3547 7.12471 28.0479 7.11569 27.9989 7.32903L27.1927 10.8419C27.1626 10.973 27.0143 11.0376 26.8978 10.9704L25.6395 10.2439C25.4958 10.1609 25.3194 10.279 25.3413 10.4435L26.053 15.7942C26.0818 16.0106 26.3872 16.0309 26.4443 15.8201L27.0884 13.4443C27.1228 13.3176 27.2678 13.2578 27.3815 13.3234L29.248 14.4011C29.3945 14.4857 29.5733 14.3615 29.5452 14.1947Z"
          fill="#FCB815"
        />
      </g>

      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="54"
          height="56"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1.5" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_9028_5150" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect2_dropShadow_9028_5150" />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0" />
          <feBlend mode="normal" in2="effect1_dropShadow_9028_5150" result="effect2_dropShadow_9028_5150" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_9028_5150" result="shape" />
        </filter>
      </defs>
    </svg>
  );
};

/**
 * BoredIcon - Bored face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const BoredIcon: React.FC<IconProps> = ({ className = '' }) => (
  <StandardEmotionIcon prefix="bored" color="#E33480" className={className}>
    <path
      d="M34.5 30C38.0898 30 41 28.2091 41 26C41 23.7909 38.0898 22 34.5 22C30.9101 22 28 23.7909 28 26C28 28.2091 30.9101 30 34.5 30Z"
      fill="white"
    />
    <path
      d="M38.2231 29.4463C40.0032 29.4463 41.4463 28.0032 41.4463 26.2232C41.4463 24.4431 40.0032 23 38.2231 23C36.4431 23 35 24.4431 35 26.2232C35 28.0032 36.4431 29.4463 38.2231 29.4463Z"
      fill="black"
    />
    <circle cx="37.466" cy="25.42" r="1.28926" fill="white" />
    <path
      d="M32.0909 25.7356C36.0071 25.7356 39.1819 24.0039 39.1819 21.8678C39.1819 19.7317 36.0071 18 32.0909 18C28.1747 18 25 19.7317 25 21.8678C25 24.0039 28.1747 25.7356 32.0909 25.7356Z"
      fill="#E33480"
    />
    <path
      d="M19.5 30C23.0898 30 26 28.2091 26 26C26 23.7909 23.0898 22 19.5 22C15.9101 22 13 23.7909 13 26C13 28.2091 15.9101 30 19.5 30Z"
      fill="white"
    />
    <path
      d="M23.2231 29.4463C25.0032 29.4463 26.4463 28.0032 26.4463 26.2232C26.4463 24.4431 25.0032 23 23.2231 23C21.4431 23 20 24.4431 20 26.2232C20 28.0032 21.4431 29.4463 23.2231 29.4463Z"
      fill="black"
    />
    <circle cx="22.466" cy="25.42" r="1.28926" fill="white" />
    <path
      d="M17.0909 25.7356C21.0071 25.7356 24.1819 24.0039 24.1819 21.8678C24.1819 19.7317 21.0071 18 17.0909 18C13.1747 18 10 19.7317 10 21.8678C10 24.0039 13.1747 25.7356 17.0909 25.7356Z"
      fill="#E33480"
    />
    <path
      d="M17.0909 25.7356C21.0071 25.7356 24.1819 24.0039 24.1819 21.8678C24.1819 19.7317 21.0071 18 17.0909 18C13.1747 18 10 19.7317 10 21.8678C10 24.0039 13.1747 25.7356 17.0909 25.7356Z"
      fill="#E33480"
    />
    <path
      d="M18.1923 18.1861L12.6888 19.6727C12.3084 19.7755 12.0833 20.1667 12.186 20.5465C12.2887 20.9263 12.6804 21.1509 13.0608 21.0482L18.5643 19.5616C18.9448 19.4588 19.1699 19.0676 19.0672 18.6878C18.9644 18.3079 18.5728 18.0833 18.1923 18.1861Z"
      fill="#000308"
    />
    <path
      d="M40.5679 19.6256L35.0465 18.2068C34.6649 18.1087 34.276 18.3381 34.1779 18.7192C34.0799 19.1002 34.3098 19.4886 34.6915 19.5867L40.2128 21.0056C40.5945 21.1037 40.9834 20.8743 41.0814 20.4932C41.1795 20.1122 40.9495 19.7237 40.5679 19.6256Z"
      fill="#000308"
    />
    <path
      d="M26.5451 34.2021C27.841 34.1733 29.1832 34.3907 30.7531 34.914C31.1722 35.0537 31.3985 35.5066 31.2589 35.9257C31.1192 36.3449 30.6664 36.5713 30.2472 36.4316C28.8173 35.9549 27.6592 35.7777 26.5802 35.8017C25.4974 35.8258 24.4522 36.0532 23.2404 36.4355C22.8192 36.5682 22.3705 36.3351 22.2375 35.914C22.1045 35.4927 22.3386 35.043 22.7599 34.9101C24.0478 34.5038 25.2531 34.2308 26.5451 34.2021Z"
      fill="#000308"
    />
  </StandardEmotionIcon>
);

/**
 * DownIcon - Down/sad face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const DownIcon: React.FC<IconProps> = ({ className = '' }) => (
  <StandardEmotionIcon prefix="down" color="#E33480" className={className}>
    <path d="M36 31C35 31.7546 32.4 32.8111 30 31" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M41.5004 27.5C39.5004 27.5 37.2121 27.2187 35.1828 25"
      stroke="black"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M13.1706 27.1642C15.1685 27.2694 17.4693 27.1088 19.6133 25"
      stroke="black"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path d="M23 31C22 31.7546 19.4 32.8111 17 31" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M24 38H30.5" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
  </StandardEmotionIcon>
);

/**
 * SadIcon - Sad face with tear SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const SadIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { filterId, maskInsideId, mask } = useIconIds('sad');
  const mask0Id = mask(0);

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#E33480' }}
    >
      <g filter={`url(#${filterId})`}>
        <IconBase maskInsideId={maskInsideId}>
          <path
            d="M34.5 30C38.0898 30 41 28.2091 41 26C41 23.7909 38.0898 22 34.5 22C30.9101 22 28 23.7909 28 26C28 28.2091 30.9101 30 34.5 30Z"
            fill="white"
          />
          <path
            d="M34.2231 29.4463C36.0032 29.4463 37.4463 28.0032 37.4463 26.2232C37.4463 24.4431 36.0032 23 34.2231 23C32.4431 23 31 24.4431 31 26.2232C31 28.0032 32.4431 29.4463 34.2231 29.4463Z"
            fill="black"
          />
          <circle cx="33.466" cy="25.42" r="1.28926" fill="white" />
          <circle cx="35.7891" cy="27.2893" r="1" fill="white" />
          <circle cx="33.5" cy="27.5" r="0.5" fill="white" />
          <path
            d="M19.5 30C23.0898 30 26 28.2091 26 26C26 23.7909 23.0898 22 19.5 22C15.9101 22 13 23.7909 13 26C13 28.2091 15.9101 30 19.5 30Z"
            fill="white"
          />
          <path
            d="M19.2231 29.4463C21.0032 29.4463 22.4463 28.0032 22.4463 26.2232C22.4463 24.4431 21.0032 23 19.2231 23C17.4431 23 16 24.4431 16 26.2232C16 28.0032 17.4431 29.4463 19.2231 29.4463Z"
            fill="black"
          />
          <circle cx="18.466" cy="25.42" r="1.28926" fill="white" />
          <circle cx="20.7891" cy="27.2893" r="1" fill="white" />
          <circle cx="18.5" cy="27.5" r="0.5" fill="white" />
          <path
            d="M24.0725 34.745C23.8271 35.085 23.4295 35.085 23.1841 34.745C22.9386 34.4049 22.9386 33.8539 23.1841 33.5139C24.0155 32.3618 25.3574 31.9652 26.5521 32.0024C27.7475 32.0396 29.0329 32.5182 29.7962 33.4878C30.0521 33.8127 30.0693 34.3631 29.8348 34.7176C29.6003 35.0721 29.2031 35.096 28.9473 34.7711C28.5015 34.2049 27.5702 33.7759 26.5238 33.7433C25.4765 33.7107 24.551 34.0819 24.0725 34.745Z"
            fill="#1B1B19"
          />
          <path d="M11 23C12 22.7321 14.5566 22.5 16 20" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M43 23C42 22.7321 39.4434 22.5 38 20" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
          <mask
            id={mask0Id}
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="14"
            y="31"
            width="6"
            height="10"
          >
            <path
              d="M16.9766 31C16.3034 31 10.6294 40.8093 16.9766 40.8093C23.3239 40.8093 17.8422 31 16.9766 31Z"
              fill="#D9D9D9"
            />
          </mask>
          <g mask={`url(#${mask0Id})`}>
            <path
              d="M16.9766 31C16.3034 31 10.6294 40.8093 16.9766 40.8093C23.3239 40.8093 17.8422 31 16.9766 31Z"
              fill="#36C2DB"
            />
            <path
              d="M18.2231 34.4972L18.6798 35.4916C18.806 35.7662 18.6856 36.091 18.411 36.2171C18.1364 36.3433 17.8116 36.2229 17.6855 35.9483L17.2288 34.954C17.1026 34.6794 17.223 34.3545 17.4976 34.2284C17.7722 34.1023 18.097 34.2227 18.2231 34.4972Z"
              fill="#B8EEFD"
            />
            <path
              d="M17.0774 38.8859C13.4229 38.8859 14.3846 34.398 15.6348 32.154C14.9616 32.9875 13.6152 35.2315 13.6152 37.5395C13.6152 40.4246 15.8271 41.2902 17.0774 41.2902C18.2314 41.2902 19.9625 40.5208 20.0586 37.5395C20.1356 35.1545 18.6802 32.8272 17.9429 31.9617C19.3854 34.2698 20.7318 38.8859 17.0774 38.8859Z"
              fill="#2CA4B9"
            />
          </g>
        </IconBase>
      </g>
      <defs>
        <IconShadowFilter filterId={filterId} />
      </defs>
    </svg>
  );
};

/**
 * SleepyIcon - Sleepy face SVG asset.
 * Uses React's useId() to generate unique IDs for filters/masks to prevent collisions
 * when multiple instances are rendered on the same page.
 */
export const SleepyIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { filterId, maskInsideId, mask } = useIconIds('sleepy');
  const mask0Id = mask(0);

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#E33480' }}
    >
      <g filter={`url(#${filterId})`}>
        <IconBase maskInsideId={maskInsideId}>
          <path d="M36 27C35 27.7546 32.4 28.8111 30 27" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M23 27C22 27.7546 19.4 28.8111 17 27" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
          <path
            d="M18.1923 22.1861L12.6888 23.6727C12.3084 23.7755 12.0833 24.1667 12.186 24.5465C12.2887 24.9263 12.6804 25.1509 13.0608 25.0482L18.5643 23.5616C18.9448 23.4588 19.1699 23.0676 19.0672 22.6878C18.9644 22.3079 18.5728 22.0833 18.1923 22.1861Z"
            fill="#000308"
          />
          <path
            d="M40.5679 23.6256L35.0465 22.2068C34.6649 22.1087 34.276 22.3381 34.1779 22.7192C34.0799 23.1002 34.3098 23.4886 34.6915 23.5867L40.2128 25.0056C40.5945 25.1037 40.9834 24.8743 41.0814 24.4932C41.1795 24.1122 40.9495 23.7237 40.5679 23.6256Z"
            fill="#000308"
          />
          <mask
            id={mask0Id}
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="23"
            y="33"
            width="8"
            height="5"
          >
            <path
              d="M31 35.5C31 36.8807 29.2091 37.5 27 37.5C24.7909 37.5 23 36.8807 23 35.5C23 34.1193 24.7909 33 27 33C29.2091 33 31 34.1193 31 35.5Z"
              fill="black"
            />
          </mask>
          <g mask={`url(#${mask0Id})`}>
            <path
              d="M31 35.5C31 36.8807 29.2091 37.5 27 37.5C24.7909 37.5 23 36.8807 23 35.5C23 34.1193 24.7909 33 27 33C29.2091 33 31 34.1193 31 35.5Z"
              fill="black"
            />
            <path
              d="M25.511 31H25.471C24.6586 31 24 31.6586 24 32.471V33.8067C24 34.6191 24.6586 35.2776 25.471 35.2776H25.511C26.3234 35.2776 26.982 34.6191 26.982 33.8067V32.471C26.982 31.6586 26.3234 31 25.511 31Z"
              fill="white"
            />
            <path
              d="M30.349 32.487C30.349 31.6657 29.6832 31 28.862 31C28.0407 31 27.375 31.6657 27.375 32.487V33.7906C27.375 34.6119 28.0407 35.2776 28.862 35.2776C29.6832 35.2776 30.349 34.6119 30.349 33.7906V32.487Z"
              fill="white"
            />
          </g>
          <path
            d="M31.6732 12.7581C33.1071 12.7581 33.8482 14.5053 32.8667 15.5717L31.6125 16.9337H34.6326C35.0847 16.9337 35.4512 17.3076 35.4512 17.7688C35.4512 18.23 35.0847 18.6039 34.6326 18.6039H31.6125C30.1788 18.6036 29.4382 16.8565 30.4197 15.7903L31.6732 14.4283H29.7211C29.269 14.4283 28.9025 14.0544 28.9025 13.5932C28.9025 13.132 29.269 12.7581 29.7211 12.7581H31.6732ZM37.5953 9C39.056 9.00033 39.785 10.8046 38.7488 11.8552L35.799 14.8459H41.1814C41.6335 14.8459 42 15.2198 42 15.681C42 16.1422 41.6335 16.5161 41.1814 16.5161H35.799C34.338 16.5161 33.6083 14.7116 34.6446 13.6609L37.5953 10.6702H33.814C33.3619 10.6702 32.9954 10.2963 32.9954 9.83512C32.9954 9.3739 33.3619 9 33.814 9H37.5953Z"
            fill="#3FDEFB"
          />
          <path
            d="M28.0407 18.6742C28.7935 17.8563 28.225 16.5162 27.1252 16.5161H25.6279C25.2811 16.5161 25 16.8029 25 17.1567C25 17.5104 25.2811 17.7972 25.6279 17.7972H27.1252L26.1638 18.8419C25.4109 19.6597 25.9789 20.9998 27.0786 21H29.3952C29.7419 21 30.023 20.7132 30.023 20.3594C30.023 20.0057 29.7419 19.7189 29.3952 19.7189H27.0786L28.0407 18.6742Z"
            fill="#3FDEFB"
          />
        </IconBase>
      </g>
      <defs>
        <IconShadowFilter filterId={filterId} />
      </defs>
    </svg>
  );
};

export const CalmIcon: React.FC<IconProps> = ({ className = '' }) => (
  <StandardEmotionIcon prefix="calm" color="#FCB815" className={className}>
    <path
      d="M30.3161 32.2582C30.6604 31.9139 31.2182 31.9139 31.5625 32.2582C31.9068 32.6025 31.9068 33.1604 31.5625 33.5047C30.3961 34.6711 28.5135 35.0727 26.8373 35.0351C25.1602 34.9973 23.3568 34.5128 22.2859 33.5311C21.927 33.2021 21.9027 32.6449 22.2318 32.2859C22.5608 31.927 23.118 31.9028 23.4769 32.2318C24.1023 32.8051 25.4089 33.2394 26.877 33.2724C28.3463 33.3054 29.6447 32.9296 30.3161 32.2582Z"
      fill="#1B1B19"
    />
    <path d="M36 25C35 25.7546 32.4 26.8111 30 25" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M23 25C22 25.7546 19.4 26.8111 17 25" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M18.1923 18.1861L12.6888 19.6727C12.3084 19.7755 12.0833 20.1667 12.186 20.5465C12.2887 20.9263 12.6804 21.1509 13.0608 21.0482L18.5643 19.5616C18.9448 19.4588 19.1699 19.0676 19.0672 18.6878C18.9644 18.3079 18.5728 18.0833 18.1923 18.1861Z"
      fill="#000308"
    />
    <path
      d="M37.1923 18.1861L42.6888 19.6727C43.0692 19.7755 43.2943 20.1667 43.1916 20.5465C43.0889 20.9263 42.6972 21.1509 42.3168 21.0482L36.8133 19.5616C36.4328 19.4588 36.2077 19.0676 36.3104 18.6878C36.4132 18.3079 36.8048 18.0833 37.1853 18.1861H37.1923Z"
      fill="#000308"
    />
  </StandardEmotionIcon>
);

export const ContentIcon: React.FC<IconProps> = ({ className = '' }) => (
  <StandardEmotionIcon prefix="content" color="#FCB815" className={className}>
    <path
      d="M21.6028 17.0822L15.9306 17.6525C15.5386 17.6919 15.2527 18.0412 15.2922 18.4327C15.3317 18.8242 15.6815 19.1096 16.0736 19.0702L21.7458 18.4999C22.1379 18.4605 22.4237 18.1112 22.3842 17.7197C22.3447 17.3282 21.9949 17.0428 21.6028 17.0822Z"
      fill="#000308"
    />
    <path
      d="M39.0002 17.6417L33.3281 17.0714C32.936 17.032 32.5862 17.3174 32.5467 17.7089C32.5072 18.1003 32.793 18.4497 33.1851 18.4891L38.8573 19.0594C39.2494 19.0988 39.5992 18.8134 39.6387 18.4219C39.6782 18.0304 39.3923 17.6811 39.0002 17.6417Z"
      fill="#000308"
    />
    <path
      d="M31.3161 32.2582C31.6604 31.9139 32.2182 31.9139 32.5625 32.2582C32.9068 32.6025 32.9068 33.1604 32.5625 33.5047C31.3961 34.6711 29.5135 35.0727 27.8373 35.0351C26.1602 34.9973 24.3568 34.5128 23.2859 33.5311C22.927 33.2021 22.9027 32.6449 23.2318 32.2859C23.5608 31.927 24.118 31.9028 24.4769 32.2318C25.1023 32.8051 26.4089 33.2394 27.877 33.2724C29.3463 33.3054 30.6447 32.9296 31.3161 32.2582Z"
      fill="#1B1B19"
    />
    <path
      d="M21.4879 30.0647C24.5187 30.0647 26.9757 27.6077 26.9757 24.5769C26.9757 21.546 24.5187 19.089 21.4879 19.089C18.457 19.089 16 21.546 16 24.5769C16 27.6077 18.457 30.0647 21.4879 30.0647Z"
      fill="white"
    />
    <path
      d="M33.6011 29.9757C36.632 29.9757 39.089 27.5187 39.089 24.4879C39.089 21.457 36.632 19 33.6011 19C30.5703 19 28.1133 21.457 28.1133 24.4879C28.1133 27.5187 30.5703 29.9757 33.6011 29.9757Z"
      fill="white"
    />
    <path
      d="M33.456 26.3193C34.7741 26.3193 35.8426 25.2508 35.8426 23.9327C35.8426 22.6146 34.7741 21.546 33.456 21.546C32.1379 21.546 31.0693 22.6146 31.0693 23.9327C31.0693 25.2508 32.1379 26.3193 33.456 26.3193Z"
      fill="#030303"
    />
    <path
      d="M21.3534 26.3193C22.6715 26.3193 23.7401 25.2508 23.7401 23.9327C23.7401 22.6146 22.6715 21.546 21.3534 21.546C20.0353 21.546 18.9668 22.6146 18.9668 23.9327C18.9668 25.2508 20.0353 26.3193 21.3534 26.3193Z"
      fill="#030303"
    />
    <path
      d="M21.9335 23.0819C22.1534 22.8789 22.1873 22.558 22.0093 22.3651C21.8313 22.1723 21.5087 22.1806 21.2888 22.3837C21.0689 22.5867 21.035 22.9076 21.213 23.1005C21.3911 23.2933 21.7136 23.285 21.9335 23.0819Z"
      fill="white"
    />
    <path
      d="M34.0371 22.993C34.2569 22.79 34.2909 22.4691 34.1128 22.2763C33.9348 22.0835 33.6122 22.0918 33.3923 22.2948C33.1724 22.4979 33.1385 22.8188 33.3165 23.0116C33.4946 23.2044 33.8172 23.1961 34.0371 22.993Z"
      fill="white"
    />
  </StandardEmotionIcon>
);

export const PleasedIcon: React.FC<IconProps> = ({ className = '' }) => (
  <StandardEmotionIcon prefix="pleased" color="#FCB815" className={className}>
    <path
      d="M35.625 33.3317C39.5885 33.3317 42.8016 30.1234 42.8016 26.1658C42.8016 22.2083 39.5885 19 35.625 19C31.6614 19 28.4482 22.2083 28.4482 26.1658C28.4482 30.1234 31.6614 33.3317 35.625 33.3317Z"
      fill="white"
    />
    <path
      d="M35.6242 30.2348C37.875 30.2348 39.6996 28.413 39.6996 26.1656C39.6996 23.9183 37.875 22.0964 35.6242 22.0964C33.3734 22.0964 31.5488 23.9183 31.5488 26.1656C31.5488 28.413 33.3734 30.2348 35.6242 30.2348Z"
      fill="#030303"
    />
    <path
      d="M33.3293 24.2649C34.022 24.2649 34.5835 23.7042 34.5835 23.0126C34.5835 22.321 34.022 21.7604 33.3293 21.7604C32.6367 21.7604 32.0752 22.321 32.0752 23.0126C32.0752 23.7042 32.6367 24.2649 33.3293 24.2649Z"
      fill="white"
    />
    <path
      d="M19.9501 33.3317C23.9137 33.3317 27.1268 30.1234 27.1268 26.1658C27.1268 22.2083 23.9137 19 19.9501 19C15.9866 19 12.7734 22.2083 12.7734 26.1658C12.7734 30.1234 15.9866 33.3317 19.9501 33.3317Z"
      fill="white"
    />
    <path
      d="M19.9504 30.2348C22.2011 30.2348 24.0257 28.413 24.0257 26.1656C24.0257 23.9183 22.2011 22.0964 19.9504 22.0964C17.6996 22.0964 15.875 23.9183 15.875 26.1656C15.875 28.413 17.6996 30.2348 19.9504 30.2348Z"
      fill="#030303"
    />
    <path
      d="M17.6457 24.2649C18.3384 24.2649 18.8999 23.7042 18.8999 23.0126C18.8999 22.321 18.3384 21.7604 17.6457 21.7604C16.9531 21.7604 16.3916 22.321 16.3916 23.0126C16.3916 23.7042 16.9531 24.2649 17.6457 24.2649Z"
      fill="white"
    />
    <path
      d="M21.6028 15.0822L15.9306 15.6525C15.5386 15.6919 15.2527 16.0412 15.2922 16.4327C15.3317 16.8242 15.6815 17.1096 16.0736 17.0702L21.7458 16.4999C22.1379 16.4605 22.4237 16.1112 22.3842 15.7197C22.3447 15.3282 21.9949 15.0428 21.6028 15.0822Z"
      fill="#000308"
    />
    <path
      d="M38.9993 15.6417L33.3271 15.0714C32.935 15.032 32.5852 15.3174 32.5457 15.7089C32.5062 16.1003 32.7921 16.4497 33.1842 16.4891L38.8563 17.0594C39.2484 17.0988 39.5982 16.8134 39.6377 16.4219C39.6772 16.0304 39.3914 15.6811 38.9993 15.6417Z"
      fill="#000308"
    />
    <path
      d="M19.6236 44.3578C24.1258 44.3578 27.7755 40.7136 27.7755 36.2182C27.7755 31.7228 24.1258 28.0786 19.6236 28.0786C15.1214 28.0786 11.4717 31.7228 11.4717 36.2182C11.4717 40.7136 15.1214 44.3578 19.6236 44.3578Z"
      fill="#FCB815"
    />
    <path
      d="M34.8482 44.3578C39.3504 44.3578 43.0002 40.7136 43.0002 36.2182C43.0002 31.7228 39.3504 28.0786 34.8482 28.0786C30.346 28.0786 26.6963 31.7228 26.6963 36.2182C26.6963 40.7136 30.346 44.3578 34.8482 44.3578Z"
      fill="#FCB815"
    />
    <circle cx="39.5" cy="30.5" r="3.5" fill="#F06767" />
    <circle cx="16.5" cy="30.5" r="3.5" fill="#F06767" />
    <path
      d="M31.3161 32.2582C31.6604 31.9139 32.2182 31.9139 32.5625 32.2582C32.9068 32.6025 32.9068 33.1604 32.5625 33.5047C31.3961 34.6711 29.5135 35.0727 27.8373 35.0351C26.1602 34.9973 24.3568 34.5128 23.2859 33.5311C22.927 33.2021 22.9027 32.6449 23.2318 32.2859C23.5608 31.927 24.118 31.9028 24.4769 32.2318C25.1023 32.8051 26.4089 33.2394 27.877 33.2724C29.3463 33.3054 30.6447 32.9296 31.3161 32.2582Z"
      fill="#1B1B19"
    />
  </StandardEmotionIcon>
);

export const WarmIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { filterId, maskInsideId, mask } = useIconIds('warm');
  const mask0Id = mask(0);
  const mask1Id = mask(1);
  const mask2Id = mask(2);

  return (
    <svg
      viewBox="0 0 54 56"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#FCB815' }}
    >
      <g filter={`url(#${filterId})`}>
        <IconBase maskInsideId={maskInsideId}>
          <path
            d="M23.6028 18.0822L17.9306 18.6525C17.5386 18.6919 17.2527 19.0412 17.2922 19.4327C17.3317 19.8242 17.6815 20.1096 18.0736 20.0702L23.7458 19.4999C24.1379 19.4605 24.4237 19.1112 24.3842 18.7197C24.3447 18.3282 23.9949 18.0428 23.6028 18.0822Z"
            fill="#000308"
          />
          <path
            d="M36.9993 18.6417L31.3271 18.0714C30.935 18.032 30.5852 18.3174 30.5457 18.7089C30.5062 19.1003 30.7921 19.4497 31.1842 19.4891L36.8563 20.0594C37.2484 20.0988 37.5982 19.8134 37.6377 19.4219C37.6772 19.0304 37.3914 18.6811 36.9993 18.6417Z"
            fill="#000308"
          />
          <path
            d="M31.3161 32.2582C31.6604 31.9139 32.2182 31.9139 32.5625 32.2582C32.9068 32.6025 32.9068 33.1604 32.5625 33.5047C31.3961 34.6711 29.5135 35.0727 27.8373 35.0351C26.1602 34.9973 24.3568 34.5128 23.2859 33.5311C22.927 33.2021 22.9027 32.6449 23.2318 32.2859C23.5608 31.927 24.118 31.9028 24.4769 32.2318C25.1023 32.8051 26.4089 33.2394 27.877 33.2724C29.3463 33.3054 30.6447 32.9296 31.3161 32.2582Z"
            fill="#1B1B19"
          />
          <mask
            id={mask0Id}
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="32"
            y="11"
            width="11"
            height="10"
          >
            <path
              d="M39.9372 11C38.1357 11 37.9073 12.4412 37.5953 12.9816C37.2833 12.4412 37.0549 11 35.0733 11C32.5512 11 32.3711 13.1617 32.3711 14.0625C32.3711 17.8455 36.8747 20.908 37.5953 20.908C38.1358 20.908 42.9997 18.0257 42.9997 14.2426C42.9997 13.1617 42.6394 11 39.9372 11Z"
              fill="#E33434"
            />
          </mask>
          <g mask={`url(#${mask0Id})`}>
            <path
              d="M39.9372 11C38.1357 11 37.9073 12.4412 37.5953 12.9816C37.2833 12.4412 37.0549 11 35.0733 11C32.5512 11 32.3711 13.1617 32.3711 14.0625C32.3711 17.8455 36.8747 20.908 37.5953 20.908C38.1358 20.908 42.9997 18.0257 42.9997 14.2426C42.9997 13.1617 42.6394 11 39.9372 11Z"
              fill="#E33434"
            />
            <path
              d="M34.5899 12.9039L34.2936 13.9572C34.2118 14.2481 33.9097 14.4176 33.6188 14.3357C33.328 14.2539 33.1585 13.9518 33.2403 13.6609L33.5366 12.6076C33.6184 12.3167 33.9205 12.1472 34.2114 12.229C34.5023 12.3109 34.6717 12.613 34.5899 12.9039Z"
              fill="#F29090"
            />
            <path
              d="M42.1048 13.8937C42.1048 13.4236 42.0615 13.0152 41.9891 12.6606C41.8122 11.7947 42.2423 11.1621 42.6866 11.926C43.125 12.6797 43.3764 13.6975 43.1857 14.7943C42.6525 17.8599 39.8184 19.929 38.0178 20.8877C37.7558 21.0273 37.4433 21.0316 37.1821 20.8903C35.34 19.8938 33.5716 17.9643 33.8395 18.2323C34.1998 18.5926 36.1814 19.3132 37.0821 19.133C37.9829 18.9529 42.1048 16.2204 42.1048 13.8937Z"
              fill="#B82828"
            />
          </g>
          <mask
            id={mask1Id}
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="33"
            y="32"
            width="11"
            height="10"
          >
            <path
              d="M40.5661 32C38.7646 32 38.5362 33.4412 38.2242 33.9816C37.9122 33.4412 37.6838 32 35.7022 32C33.1801 32 33 34.1617 33 35.0625C33 38.8455 37.5036 41.908 38.2242 41.908C38.7647 41.908 43.6286 39.0257 43.6286 35.2426C43.6286 34.1617 43.2683 32 40.5661 32Z"
              fill="#E33434"
            />
          </mask>
          <g mask={`url(#${mask1Id})`}>
            <path
              d="M40.5661 32C38.7646 32 38.5362 33.4412 38.2242 33.9816C37.9122 33.4412 37.6838 32 35.7022 32C33.1801 32 33 34.1617 33 35.0625C33 38.8455 37.5036 41.908 38.2242 41.908C38.7647 41.908 43.6286 39.0257 43.6286 35.2426C43.6286 34.1617 43.2683 32 40.5661 32Z"
              fill="#E33434"
            />
            <path
              d="M35.2188 33.9039L34.9226 34.9572C34.8407 35.2481 34.5386 35.4176 34.2477 35.3357C33.9569 35.2539 33.7874 34.9518 33.8692 34.6609L34.1655 33.6076C34.2473 33.3167 34.5494 33.1472 34.8403 33.229C35.1312 33.3109 35.3006 33.613 35.2188 33.9039Z"
              fill="#F29090"
            />
            <path
              d="M42.7337 34.8937C42.7337 34.4236 42.6904 34.0152 42.618 33.6606C42.4411 32.7947 42.8712 32.1621 43.3156 32.926C43.754 33.6797 44.0053 34.6975 43.8146 35.7943C43.2814 38.8599 40.4473 40.929 38.6467 41.8877C38.3847 42.0273 38.0722 42.0316 37.811 41.8903C35.9689 40.8938 34.2005 38.9643 34.4684 39.2323C34.8287 39.5926 36.8103 40.3132 37.711 40.133C38.6118 39.9529 42.7337 37.2204 42.7337 34.8937Z"
              fill="#B82828"
            />
          </g>
          <mask
            id={mask2Id}
            style={{ maskType: 'alpha' }}
            maskUnits="userSpaceOnUse"
            x="12"
            y="28"
            width="11"
            height="10"
          >
            <path
              d="M19.5661 28C17.7646 28 17.5362 29.4412 17.2242 29.9816C16.9122 29.4412 16.6838 28 14.7022 28C12.1801 28 12 30.1617 12 31.0625C12 34.8455 16.5036 37.908 17.2242 37.908C17.7647 37.908 22.6286 35.0257 22.6286 31.2426C22.6286 30.1617 22.2683 28 19.5661 28Z"
              fill="#E33434"
            />
          </mask>
          <g mask={`url(#${mask2Id})`}>
            <path
              d="M19.5661 28C17.7646 28 17.5362 29.4412 17.2242 29.9816C16.9122 29.4412 16.6838 28 14.7022 28C12.1801 28 12 30.1617 12 31.0625C12 34.8455 16.5036 37.908 17.2242 37.908C17.7647 37.908 22.6286 35.0257 22.6286 31.2426C22.6286 30.1617 22.2683 28 19.5661 28Z"
              fill="#E33434"
            />
            <path
              d="M14.2188 29.9039L13.9226 30.9572C13.8407 31.2481 13.5386 31.4176 13.2477 31.3357C12.9569 31.2539 12.7874 30.9518 12.8692 30.6609L13.1655 29.6076C13.2473 29.3167 13.5494 29.1472 13.8403 29.229C14.1312 29.3109 14.3006 29.613 14.2188 29.9039Z"
              fill="#F29090"
            />
            <path
              d="M21.7337 30.8937C21.7337 30.4236 21.6904 30.0152 21.618 29.6606C21.4411 28.7947 21.8712 28.1621 22.3156 28.926C22.754 29.6797 23.0053 30.6975 22.8146 31.7943C22.2814 34.8599 19.4473 36.929 17.6467 37.8877C17.3847 38.0273 17.0722 38.0316 16.811 37.8903C14.9689 36.8938 13.2005 34.9643 13.4684 35.2323C13.8287 35.5926 15.8103 36.3132 16.711 36.133C17.6118 35.9529 21.7337 33.2204 21.7337 30.8937Z"
              fill="#B82828"
            />
          </g>
          <path d="M19.5 25C20.3333 24.5 22.5 23.8 24.5 25" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
          <path d="M31 25C31.8333 24.5 34 23.8 36 25" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
        </IconBase>
      </g>
      <defs>
        <IconShadowFilter filterId={filterId} />
      </defs>
    </svg>
  );
};

export const CheerfulIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { maskInsideId, mask } = useIconIds('cheerful');
  const mask0Id = mask(0);

  return (
    <svg
      viewBox="0 0 48 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#03BD7D' }}
    >
      <SmallIconBase maskInsideId={maskInsideId}>
        <path
          d="M16.1083 28H31.8906C32.5029 28 33 28.2169 33 28.4841V30.073C33 32.2404 28.9674 34 24 34C19.0326 34 15 32.2404 15 30.073V28.4836C15 28.2164 15.4971 28 16.1083 28Z"
          fill="#030303"
        />
        <mask
          id={mask0Id}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="15"
          y="28"
          width="18"
          height="6"
        >
          <path
            d="M16.1083 28H31.8906C32.5029 28 33 28.2169 33 28.4841V30.073C33 32.2404 28.9674 34 24 34C19.0326 34 15 32.2404 15 30.073V28.4836C15 28.2164 15.4971 28 16.1083 28Z"
            fill="white"
          />
        </mask>
        <g mask={`url(#${mask0Id})`}>
          <path
            d="M18.511 31H18.471C17.6586 31 17 31.6586 17 32.471V33.8067C17 34.6191 17.6586 35.2776 18.471 35.2776H18.511C19.3234 35.2776 19.982 34.6191 19.982 33.8067V32.471C19.982 31.6586 19.3234 31 18.511 31Z"
            fill="white"
          />
          <path
            d="M23.4564 32.487C23.4564 31.6657 22.7906 31 21.9694 31C21.1482 31 20.4824 31.6657 20.4824 32.487V33.7906C20.4824 34.6119 21.1482 35.2776 21.9694 35.2776C22.7906 35.2776 23.4564 34.6119 23.4564 33.7906V32.487Z"
            fill="white"
          />
          <path
            d="M25.4671 31H25.427C24.6146 31 23.9561 31.6586 23.9561 32.471V33.8067C23.9561 34.6191 24.6146 35.2776 25.427 35.2776H25.4671C26.2795 35.2776 26.938 34.6191 26.938 33.8067V32.471C26.938 31.6586 26.2795 31 25.4671 31Z"
            fill="white"
          />
          <path
            d="M30.4115 32.487C30.4115 31.6657 29.7457 31 28.9245 31C28.1032 31 27.4375 31.6657 27.4375 32.487V33.7906C27.4375 34.6119 28.1032 35.2776 28.9245 35.2776C29.7457 35.2776 30.4115 34.6119 30.4115 33.7906V32.487Z"
            fill="white"
          />
          <path
            d="M18.511 25H18.471C17.6586 25 17 25.6586 17 26.471V27.8067C17 28.6191 17.6586 29.2776 18.471 29.2776H18.511C19.3234 29.2776 19.982 28.6191 19.982 27.8067V26.471C19.982 25.6586 19.3234 25 18.511 25Z"
            fill="white"
          />
          <path
            d="M23.4564 27.487C23.4564 26.6657 22.7906 26 21.9694 26C21.1482 26 20.4824 26.6657 20.4824 27.487V28.7906C20.4824 29.6119 21.1482 30.2776 21.9694 30.2776C22.7906 30.2776 23.4564 29.6119 23.4564 28.7906V27.487Z"
            fill="white"
          />
          <path
            d="M25.4671 26H25.427C24.6146 26 23.9561 26.6586 23.9561 27.471V28.8067C23.9561 29.6191 24.6146 30.2776 25.427 30.2776H25.4671C26.2795 30.2776 26.938 29.6191 26.938 28.8067V27.471C26.938 26.6586 26.2795 26 25.4671 26Z"
            fill="white"
          />
          <path
            d="M30.4115 26.487C30.4115 25.6657 29.7457 25 28.9245 25C28.1032 25 27.4375 25.6657 27.4375 26.487V27.7906C27.4375 28.6119 28.1032 29.2776 28.9245 29.2776C29.7457 29.2776 30.4115 28.6119 30.4115 27.7906V26.487Z"
            fill="white"
          />
        </g>
        <path d="M16 20C16 16 21 16 21 20" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M27 20C27 16 32 16 32 20" stroke="black" strokeWidth="1.6" strokeLinecap="round" />
      </SmallIconBase>
    </svg>
  );
};

export const AmusedIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { maskInsideId, mask } = useIconIds('amused');
  const mask0Id = mask(0);

  return (
    <svg
      viewBox="0 0 48 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#03BD7D' }}
    >
      <SmallIconBase maskInsideId={maskInsideId}>
        <path
          d="M17.1083 29H32.8906C33.5029 29 34 29.3253 34 29.7261V32.1095C34 35.3607 29.9674 38 25 38C20.0326 38 16 35.3607 16 32.1095V29.7254C16 29.3246 16.4971 29 17.1083 29Z"
          fill="#030303"
        />
        <mask
          id={mask0Id}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="16"
          y="29"
          width="18"
          height="9"
        >
          <path
            d="M17.1083 29H32.8906C33.5029 29 34 29.3253 34 29.7261V32.1095C34 35.3607 29.9674 38 25 38C20.0326 38 16 35.3607 16 32.1095V29.7254C16 29.3246 16.4971 29 17.1083 29Z"
            fill="white"
          />
        </mask>
        <g mask={`url(#${mask0Id})`}>
          <path
            d="M23.1253 27.4604H23.0852C22.2728 27.4604 21.6143 28.119 21.6143 28.9314V30.2671C21.6143 31.0795 22.2728 31.7381 23.0852 31.7381H23.1253C23.9377 31.7381 24.5962 31.0795 24.5962 30.2671V28.9314C24.5962 28.119 23.9377 27.4604 23.1253 27.4604Z"
            fill="white"
          />
          <path
            d="M27.9632 28.9474C27.9632 28.1262 27.2975 27.4604 26.4762 27.4604C25.655 27.4604 24.9893 28.1262 24.9893 28.9474V30.2511C24.9893 31.0723 25.655 31.7381 26.4762 31.7381C27.2975 31.7381 27.9632 31.0723 27.9632 30.2511V28.9474Z"
            fill="white"
          />
        </g>
        <path
          d="M35.5 21.091L29.6049 19.5114C28.6166 19.2466 28.6166 17.8444 29.6049 17.5796L35.5 16"
          stroke="black"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path
          d="M14 16L19.8951 17.5796C20.8834 17.8444 20.8834 19.2467 19.8951 19.5115L14 21.0911"
          stroke="black"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </SmallIconBase>
    </svg>
  );
};

export const HappyIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { maskInsideId, mask } = useIconIds('happy');
  const mask0Id = mask(0);

  return (
    <svg
      viewBox="0 0 48 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#03BD7D' }}
    >
      <SmallIconBase maskInsideId={maskInsideId}>
        <path
          d="M18.4459 25.9308C21.4767 25.9308 23.9337 23.4738 23.9337 20.4429C23.9337 17.4121 21.4767 14.9551 18.4459 14.9551C15.415 14.9551 12.958 17.4121 12.958 20.4429C12.958 23.4738 15.415 25.9308 18.4459 25.9308Z"
          fill="white"
        />
        <path
          d="M30.5591 25.8417C33.59 25.8417 36.047 23.3847 36.047 20.3538C36.047 17.323 33.59 14.866 30.5591 14.866C27.5283 14.866 25.0713 17.323 25.0713 20.3538C25.0713 23.3847 27.5283 25.8417 30.5591 25.8417Z"
          fill="white"
        />
        <path
          d="M30.414 22.1852C31.7321 22.1852 32.8006 21.1166 32.8006 19.7985C32.8006 18.4804 31.7321 17.4119 30.414 17.4119C29.0959 17.4119 28.0273 18.4804 28.0273 19.7985C28.0273 21.1166 29.0959 22.1852 30.414 22.1852Z"
          fill="#030303"
        />
        <path
          d="M18.3114 22.1852C19.6295 22.1852 20.6981 21.1166 20.6981 19.7985C20.6981 18.4804 19.6295 17.4119 18.3114 17.4119C16.9933 17.4119 15.9248 18.4804 15.9248 19.7985C15.9248 21.1166 16.9933 22.1852 18.3114 22.1852Z"
          fill="#030303"
        />
        <path
          d="M18.8915 18.948C19.1114 18.7449 19.1453 18.424 18.9673 18.2312C18.7893 18.0384 18.4667 18.0467 18.2468 18.2498C18.0269 18.4528 17.993 18.7737 18.171 18.9665C18.3491 19.1594 18.6717 19.151 18.8915 18.948Z"
          fill="white"
        />
        <path
          d="M30.9951 18.8591C31.2149 18.6561 31.2489 18.3352 31.0708 18.1424C30.8928 17.9496 30.5702 17.9579 30.3503 18.1609C30.1304 18.364 30.0965 18.6849 30.2745 18.8777C30.4526 19.0705 30.7752 19.0622 30.9951 18.8591Z"
          fill="white"
        />
        <path
          d="M18.8086 12.1215L12.7574 13.0924C12.3391 13.1595 12.0544 13.553 12.1215 13.9713C12.1886 14.3895 12.5821 14.6742 13.0004 14.6071L19.0516 13.6363C19.4699 13.5691 19.7546 13.1757 19.6875 12.7574C19.6204 12.3391 19.2269 12.0544 18.8086 12.1215Z"
          fill="#000308"
        />
        <path
          d="M36.3723 12.8045L30.3053 11.938C29.8859 11.8781 29.4973 12.1696 29.4375 12.589C29.3776 13.0083 29.669 13.3969 30.0884 13.4568L36.1554 14.3232C36.5748 14.3831 36.9633 14.0917 37.0232 13.6723C37.0831 13.2529 36.7917 12.8644 36.3723 12.8045Z"
          fill="#000308"
        />
        <path
          d="M17.1083 29H32.8906C33.5029 29 34 29.3253 34 29.7261V32.1095C34 35.3607 29.9674 38 25 38C20.0326 38 16 35.3607 16 32.1095V29.7254C16 29.3246 16.4971 29 17.1083 29Z"
          fill="#030303"
        />
        <mask
          id={mask0Id}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="16"
          y="29"
          width="18"
          height="9"
        >
          <path
            d="M17.1083 29H32.8906C33.5029 29 34 29.3253 34 29.7261V32.1095C34 35.3607 29.9674 38 25 38C20.0326 38 16 35.3607 16 32.1095V29.7254C16 29.3246 16.4971 29 17.1083 29Z"
            fill="white"
          />
        </mask>
        <g mask={`url(#${mask0Id})`}>
          <path
            d="M23.1253 27.4604H23.0852C22.2728 27.4604 21.6143 28.119 21.6143 28.9314V30.2671C21.6143 31.0795 22.2728 31.7381 23.0852 31.7381H23.1253C23.9377 31.7381 24.5962 31.0795 24.5962 30.2671V28.9314C24.5962 28.119 23.9377 27.4604 23.1253 27.4604Z"
            fill="white"
          />
          <path
            d="M27.9632 28.9474C27.9632 28.1262 27.2975 27.4604 26.4762 27.4604C25.655 27.4604 24.9893 28.1262 24.9893 28.9474V30.2511C24.9893 31.0723 25.655 31.7381 26.4762 31.7381C27.2975 31.7381 27.9632 31.0723 27.9632 30.2511V28.9474Z"
            fill="white"
          />
        </g>
      </SmallIconBase>
    </svg>
  );
};

export const ThrilledIcon: React.FC<IconProps> = ({ className = '' }) => {
  const { maskInsideId, mask } = useIconIds('trilled');
  const mask0Id = mask(0);
  const mask1Id = mask(1);
  const mask2Id = mask(2);

  return (
    <svg
      viewBox="0 0 48 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      style={{ color: '#03BD7D' }}
    >
      <SmallIconBase maskInsideId={maskInsideId}>
        <path
          d="M18.8188 27.7207H30.8829C31.351 27.7207 31.7309 28.1007 31.7309 28.5688V31.3523C31.7309 35.1494 28.6484 38.2319 24.8513 38.2319C21.0542 38.2319 17.9717 35.1494 17.9717 31.3523V28.5679C17.9717 28.0998 18.3517 27.7207 18.8188 27.7207Z"
          fill="#030303"
        />
        <mask
          id={mask0Id}
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x="17"
          y="27"
          width="15"
          height="12"
        >
          <path
            d="M18.8188 27.7207H30.8829C31.351 27.7207 31.7309 28.1007 31.7309 28.5688V31.3523C31.7309 35.1494 28.6484 38.2319 24.8513 38.2319C21.0542 38.2319 17.9717 35.1494 17.9717 31.3523V28.5679C17.9717 28.0998 18.3517 27.7207 18.8188 27.7207Z"
            fill="white"
          />
        </mask>
        <g mask={`url(#${mask0Id})`}>
          <path
            d="M23.097 26.1812H23.0569C22.2445 26.1812 21.5859 26.8397 21.5859 27.6521V28.9878C21.5859 29.8002 22.2445 30.4588 23.0569 30.4588H23.097C23.9093 30.4588 24.5679 29.8002 24.5679 28.9878V27.6521C24.5679 26.8397 23.9093 26.1812 23.097 26.1812Z"
            fill="white"
          />
          <path
            d="M27.9349 27.6681C27.9349 26.8469 27.2692 26.1812 26.4479 26.1812C25.6267 26.1812 24.9609 26.8469 24.9609 27.6681V28.9718C24.9609 29.793 25.6267 30.4588 26.4479 30.4588C27.2692 30.4588 27.9349 29.793 27.9349 28.9718V27.6681Z"
            fill="white"
          />
        </g>
        <mask id={mask1Id} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="24" y="9" width="17" height="18">
          <path
            d="M33.0378 9.53855C33.2758 9.11669 33.9146 9.24582 33.97 9.727L34.5838 15.0551C34.6086 15.2703 34.7691 15.445 34.9814 15.4879L40.2384 16.5507C40.7132 16.6466 40.7877 17.2941 40.3472 17.4955L35.4696 19.7257C35.2726 19.8158 35.156 20.0224 35.1808 20.2376L35.7946 25.5657C35.85 26.0469 35.2573 26.3179 34.9297 25.9612L31.3013 22.0114C31.1548 21.8519 30.9222 21.8049 30.7252 21.895L25.8476 24.1252C25.4071 24.3266 24.9662 23.8467 25.2042 23.4248L27.8394 18.7535C27.9458 18.5649 27.9187 18.3291 27.7721 18.1696L24.1438 14.2199C23.8161 13.8632 24.1363 13.2955 24.6111 13.3915L29.8681 14.4542C30.0804 14.4972 30.2962 14.3985 30.4026 14.2098L33.0378 9.53855Z"
            fill="#FCB815"
          />
        </mask>
        <g mask={`url(#${mask1Id})`}>
          <path
            d="M33.0378 9.53855C33.2758 9.11669 33.9146 9.24582 33.97 9.727L34.5838 15.0551C34.6086 15.2703 34.7691 15.445 34.9814 15.4879L40.2384 16.5507C40.7132 16.6466 40.7877 17.2941 40.3472 17.4955L35.4696 19.7257C35.2726 19.8158 35.156 20.0224 35.1808 20.2376L35.7946 25.5657C35.85 26.0469 35.2573 26.3179 34.9297 25.9612L31.3013 22.0114C31.1548 21.8519 30.9222 21.8049 30.7252 21.895L25.8476 24.1252C25.4071 24.3266 24.9662 23.8467 25.2042 23.4248L27.8394 18.7535C27.9458 18.5649 27.9187 18.3291 27.7721 18.1696L24.1438 14.2199C23.8161 13.8632 24.1363 13.2955 24.6111 13.3915L29.8681 14.4542C30.0804 14.4972 30.2962 14.3985 30.4026 14.2098L33.0378 9.53855Z"
            fill="#FCB815"
          />
          <path d="M30.5 22C30.1 16.8 33 13.5 34.5 12.5L34 9L24 13.5L25 24.5L30.5 22Z" fill="#FFD879" />
          <path
            d="M29.4981 15.823L29.2018 16.8764C29.12 17.1673 28.8179 17.3368 28.527 17.2549C28.2362 17.1731 28.0667 16.871 28.1485 16.5801L28.4448 15.5268C28.5266 15.2359 28.8287 15.0664 29.1196 15.1482C29.4105 15.23 29.5799 15.5322 29.4981 15.823Z"
            fill="#FFECBF"
          />
        </g>
        <mask id={mask2Id} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="7" y="9" width="18" height="18">
          <path
            d="M17.0085 9.53855C17.2465 9.11669 17.8853 9.24582 17.9407 9.727L18.5545 15.0551C18.5793 15.2703 18.7398 15.445 18.9521 15.4879L24.2091 16.5507C24.6839 16.6466 24.7584 17.2941 24.3179 17.4955L19.4403 19.7257C19.2433 19.8158 19.1267 20.0224 19.1515 20.2376L19.7653 25.5657C19.8207 26.0469 19.228 26.3179 18.9004 25.9612L15.272 22.0114C15.1255 21.8519 14.8929 21.8049 14.6959 21.895L9.81828 24.1252C9.37778 24.3266 8.93689 23.8467 9.17488 23.4248L11.8101 18.7535C11.9165 18.5649 11.8894 18.3291 11.7428 18.1696L8.11449 14.2199C7.78682 13.8632 8.10703 13.2955 8.58179 13.3915L13.8388 14.4542C14.0511 14.4972 14.2669 14.3985 14.3733 14.2098L17.0085 9.53855Z"
            fill="#FCB815"
          />
        </mask>
        <g mask={`url(#${mask2Id})`}>
          <path
            d="M17.0085 9.53855C17.2465 9.11669 17.8853 9.24582 17.9407 9.727L18.5545 15.0551C18.5793 15.2703 18.7398 15.445 18.9521 15.4879L24.2091 16.5507C24.6839 16.6466 24.7584 17.2941 24.3179 17.4955L19.4403 19.7257C19.2433 19.8158 19.1267 20.0224 19.1515 20.2376L19.7653 25.5657C19.8207 26.0469 19.228 26.3179 18.9004 25.9612L15.272 22.0114C15.1255 21.8519 14.8929 21.8049 14.6959 21.895L9.81828 24.1252C9.37778 24.3266 8.93689 23.8467 9.17488 23.4248L11.8101 18.7535C11.9165 18.5649 11.8894 18.3291 11.7428 18.1696L8.11449 14.2199C7.78682 13.8632 8.10703 13.2955 8.58179 13.3915L13.8388 14.4542C14.0511 14.4972 14.2669 14.3985 14.3733 14.2098L17.0085 9.53855Z"
            fill="#FCB815"
          />
          <path
            d="M14.4707 22C14.0707 16.8 16.9707 13.5 18.4707 12.5L17.9707 9L7.9707 13.5L8.9707 24.5L14.4707 22Z"
            fill="#FFD879"
          />
        </g>
        <path
          d="M13.4981 15.823L13.2018 16.8764C13.12 17.1673 12.8179 17.3368 12.527 17.2549C12.2362 17.1731 12.0667 16.871 12.1485 16.5801L12.4448 15.5268C12.5266 15.2359 12.8287 15.0664 13.1196 15.1482C13.4105 15.23 13.5799 15.5322 13.4981 15.823Z"
          fill="#FFECBF"
        />
      </SmallIconBase>
    </svg>
  );
};

export const DEFAULT_GRID: Cell[] = [
  {
    id: 'annoyed',
    icon: AnnoyedIcon,
    label: 'Annoyed',
    valence: -1,
    arousal: 1,
    message: 'Got it. Thanks for sharing.',
  },
  {
    id: 'angry',
    icon: AngryFaceIcon,
    label: 'Angry',
    valence: -1,
    arousal: 2,
    message: 'Big feelings happen. We hear you.',
  },
  {
    id: 'anxious',
    icon: AnxiousIcon,
    label: 'Anxious',
    valence: -2,
    arousal: 1,
    message: 'Thanks for checking in.',
  },
  {
    id: 'panicked',
    icon: PanickedIcon,
    label: 'Panicked / Afraid',
    valence: -2,
    arousal: 2,
    message: 'That sounds intense. Thanks for sharing.',
  },
  {
    id: 'sleepy',
    icon: SleepyIcon,
    label: 'Sleepy',
    valence: -1,
    arousal: -2,
    message: 'Feeling low energy today.',
  },
  {
    id: 'bored',
    icon: BoredIcon,
    label: 'Bored / Unimpressed',
    valence: -1,
    arousal: -1,
    message: 'Alright. Thanks for letting us know.',
  },
  {
    id: 'down',
    icon: DownIcon,
    label: 'Down / Blue',
    valence: -2,
    arousal: -1,
    message: 'Thanks for sharing how you feel.',
  },
  {
    id: 'sad',
    icon: SadIcon,
    label: 'Sad',
    valence: -2,
    arousal: -2,
    message: "Sorry you're feeling that way.",
  },
  {
    id: 'content',
    icon: ContentIcon,
    label: 'Content',
    valence: 1,
    arousal: -2,
    message: 'Good to hear.',
  },
  {
    id: 'calm',
    icon: CalmIcon,
    label: 'Calm / Relaxed',
    valence: 1,
    arousal: -1,
    message: 'Nice. Feeling calm is good.',
  },
  {
    id: 'pleased',
    icon: PleasedIcon,
    label: 'Pleased',
    valence: 2,
    arousal: -2,
    message: "Glad you're feeling good.",
  },
  {
    id: 'warm',
    icon: WarmIcon,
    label: 'Warm / Affectionate',
    valence: 2,
    arousal: -1,
    message: "That's a nice feeling.",
  },
  {
    id: 'cheerful',
    icon: CheerfulIcon,
    label: 'Cheerful',
    valence: 1,
    arousal: 2,
    message: 'Love that energy.',
  },
  {
    id: 'amused',
    icon: AmusedIcon,
    label: 'Amused',
    valence: 2,
    arousal: 1,
    message: 'Glad something made you smile.',
  },
  {
    id: 'happy',
    icon: HappyIcon,
    label: 'Happy',
    valence: 2,
    arousal: 2,
    message: 'Happy to hear that.',
  },
  {
    id: 'thrilled',
    icon: ThrilledIcon,
    label: 'Thrilled',
    valence: 1,
    arousal: 1,
    message: 'Awesome! Thanks for sharing.',
  },
];
